import type { MutableRefObject } from "react";
import { Box, Grid, FormControl, FormLabel, Option, Switch } from "@mui/joy";
import DarkCard from "../../../common/DarkCard";
import DarkTypography from "../../../common/DarkTypography";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import type { TrainingTemplate } from "../../../../api/training/Training.typed";
import type { QualificationItem } from "../../../../api/qualification/Qualification.typed";

type FinalProps = {
  selected: TrainingTemplate;
  sortedQualifications: QualificationItem[];
  canEdit: boolean;
  onDestinationQualificationChange: (qualificationId: number) => void;
};

export function TrainingTemplateFinalQualificationCard(props: FinalProps) {
  const { selected, sortedQualifications, canEdit, onDestinationQualificationChange } = props;

  return (
    <Grid xs={12}>
      <DarkCard dense sx={{ mt: 2 }}>
        <DarkTypography colorVariant="accent" sx={{ mb: 1 }}>
          Qualifications
        </DarkTypography>
        <FormControl sx={{ minWidth: 0 }}>
          <FormLabel>Qualification finale</FormLabel>
          <DarkSelect
            placeholder="Non définie"
            value={
              selected.DestinationQualificationID != null && selected.DestinationQualificationID > 0
                ? selected.DestinationQualificationID
                : 0
            }
            onChange={(_event, sel) => {
              const next = typeof sel === "number" && sel > 0 ? sel : 0;
              onDestinationQualificationChange(next);
            }}
            disabled={!canEdit}
          >
            <Option value={0}>Non définie</Option>
            {sortedQualifications.map((qu) => (
              <Option key={qu.id} value={qu.id}>
                {qu.sigle}
                {qu.name ? ` · ${qu.name}` : ""}
              </Option>
            ))}
          </DarkSelect>
        </FormControl>
      </DarkCard>
    </Grid>
  );
}

type ExternalProps = {
  selected: TrainingTemplate;
  inputRefs: MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
  onFieldWithFocus: (
    field: keyof Omit<TrainingTemplate, "ID">,
    value: string | number | boolean,
  ) => void;
};

export function TrainingTemplateExternalIdentifiersCard(props: ExternalProps) {
  const { selected, inputRefs, onFieldWithFocus } = props;

  return (
    <Grid xs={12}>
      <DarkCard dense sx={{ mt: 2 }}>
        <DarkTypography colorVariant="accent" sx={{ mb: 1 }}>
          Identifiants externes
        </DarkTypography>
        <Grid container spacing={2}>
          <Grid xs={12} sm={4}>
            <FormControl sx={{ minWidth: 0 }}>
              <FormLabel>ID template</FormLabel>
              <DarkInput value={selected.ID} readOnly fullWidth />
            </FormControl>
          </Grid>
          <Grid xs={12} sm={4}>
            <FormControl sx={{ minWidth: 0 }}>
              <FormLabel>Zuurit TrainingID</FormLabel>
              <DarkInput
                ref={(element) => {
                  inputRefs.current.ZTrainingId = element;
                }}
                value={selected.ZTrainingId || ""}
                onChange={(event) => onFieldWithFocus("ZTrainingId", Number(event.target.value))}
                fullWidth
                type="number"
              />
            </FormControl>
          </Grid>
          <Grid xs={12} sm={4}>
            <FormControl sx={{ minWidth: 0 }}>
              <FormLabel>Zuurit QualificationID</FormLabel>
              <DarkInput
                ref={(element) => {
                  inputRefs.current.ZQualificationId = element;
                }}
                value={selected.ZQualificationId || ""}
                onChange={(event) =>
                  onFieldWithFocus("ZQualificationId", Number(event.target.value))
                }
                fullWidth
                type="number"
              />
            </FormControl>
          </Grid>
        </Grid>
      </DarkCard>
    </Grid>
  );
}

type TheoryProps = {
  selected: TrainingTemplate;
  onNeedsTheory: (value: boolean) => void;
  onNeedsRifap: (value: boolean) => void;
};

export function TrainingTemplateTheoryCard(props: TheoryProps) {
  const { selected, onNeedsTheory, onNeedsRifap } = props;

  return (
    <Grid xs={12}>
      <DarkCard dense sx={{ mt: 2 }}>
        <DarkTypography colorVariant="accent" sx={{ mb: 1 }}>
          Théorie
        </DarkTypography>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <DarkTypography>Cours théorique requis</DarkTypography>
              <Switch
                checked={!!selected.NeedsTheory}
                onChange={(event) => onNeedsTheory(event.target.checked)}
                color={selected.NeedsTheory ? "success" : "neutral"}
              />
            </Box>
          </Grid>
          <Grid xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <DarkTypography>Rifap requis</DarkTypography>
              <Switch
                checked={!!selected.NeedsRifap}
                onChange={(event) => onNeedsRifap(event.target.checked)}
                color={selected.NeedsRifap ? "success" : "neutral"}
              />
            </Box>
          </Grid>
        </Grid>
      </DarkCard>
    </Grid>
  );
}
